/**
 * Entries Page
 *
 * Lists all captured entries with filtering and search.
 */

import { useState } from 'react';
import { useEntries, useEntryStats } from '@/hooks/use-entries';
import {
  EntryList,
  EntryFiltersBar,
  SearchBar,
} from '@/components/entries';
import type { EntryFilters } from '@/types/entry';
import { ENTRY_TYPE_ICONS } from '@/types/entry';

export function EntriesPage() {
  const [filters, setFilters] = useState<EntryFilters>({
    sortBy: 'recordedAt',
    sortOrder: 'desc',
  });

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useEntries(filters);

  const { data: stats } = useEntryStats();

  const entries = data?.pages.flatMap((page) => page.entries) ?? [];

  const handleSearchChange = (search: string) => {
    setFilters((prev) => ({ ...prev, search: search || undefined }));
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Entries</h1>
        <p className="text-gray-600 mt-1">
          Browse and manage your captured content
        </p>
      </div>

      {/* Stats summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">Total Entries</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-indigo-600">{stats.withOverrides}</div>
            <div className="text-sm text-gray-500">User Edited</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 col-span-2">
            <div className="flex flex-wrap gap-2 mb-1">
              {Object.entries(stats.byType).slice(0, 4).map(([type, count]) => (
                <span key={type} className="text-sm">
                  {ENTRY_TYPE_ICONS[type as keyof typeof ENTRY_TYPE_ICONS] || '📄'} {count}
                </span>
              ))}
            </div>
            <div className="text-sm text-gray-500">By Type</div>
          </div>
        </div>
      )}

      {/* Search */}
      <SearchBar
        value={filters.search || ''}
        onChange={handleSearchChange}
        className="mb-4"
      />

      {/* Filters */}
      <div className="mb-6">
        <EntryFiltersBar
          filters={filters}
          onFiltersChange={setFilters}
        />
      </div>

      {/* Results count */}
      {data?.pages[0]?.total !== undefined && (
        <p className="text-sm text-gray-500 mb-4">
          {data.pages[0].total} entr{data.pages[0].total !== 1 ? 'ies' : 'y'} found
        </p>
      )}

      {/* Entry list */}
      <EntryList
        entries={entries}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage ?? false}
        fetchNextPage={fetchNextPage}
      />
    </div>
  );
}
